import { useState } from 'react';
import {
  Badge,
  Button,
  Group,
  Modal,
  Paper,
  ScrollArea,
  Select,
  Stack,
  Table,
  Text,
  TextInput,
  Textarea,
  Tooltip,
} from '@mantine/core';
import { useForm, zodResolver } from '@mantine/form';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { notifications } from '@mantine/notifications';
import { IconCards, IconPlus } from '@tabler/icons-react';
import {
  ACCESS_RULE_LABELS,
  DECK_STATUS_LABELS,
  createDeckSchema,
  type CreateDeckInput,
} from '@creativitas/shared';
import { useNavigate } from 'react-router-dom';
import { PageHeader } from '@/components/ui/PageHeader';
import { EmptyState, ErrorState, LoadingState } from '@/components/ui/StateViews';
import { AssetImage } from '@/components/ui/AssetImage';
import { toUserMessage } from '@/lib/api';
import { ADMIN_DECKS_KEY, createDeck, fetchAdminDecks } from './content-api';

/** Deck catalogue for the admin (§18, §48). Drafts included. */
export function AdminDecksPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [creating, setCreating] = useState(false);

  const query = useQuery({ queryKey: ADMIN_DECKS_KEY, queryFn: fetchAdminDecks });

  const form = useForm<CreateDeckInput>({
    initialValues: { title: '', description: null, kind: 'generic' },
    validate: zodResolver(createDeckSchema),
  });

  const createMutation = useMutation({
    mutationFn: createDeck,
    onSuccess: (deck) => {
      void queryClient.invalidateQueries({ queryKey: ADMIN_DECKS_KEY });
      setCreating(false);
      form.reset();
      notifications.show({ message: `Колода «${deck.title}» создана`, color: 'teal' });
      void navigate(`/admin/decks/${deck.id}`);
    },
    onError: (error) =>
      notifications.show({
        title: 'Не удалось создать колоду',
        message: toUserMessage(error),
        color: 'red',
      }),
  });

  const items = query.data?.items ?? [];

  return (
    <>
      <PageHeader
        eyebrow="Администрирование"
        title="Колоды"
        description="Карты, порядок, правила доступа и публикация. Новая обычная колода сразу становится доступной на свободном столе."
        actions={
          <Button leftSection={<IconPlus size={16} />} onClick={() => setCreating(true)}>
            Новая колода
          </Button>
        }
      />

      {query.isLoading ? (
        <LoadingState label="Загружаем колоды..." />
      ) : query.isError ? (
        <ErrorState onRetry={() => void query.refetch()} />
      ) : items.length === 0 ? (
        <EmptyState
          icon={<IconCards size={28} stroke={1.5} />}
          title="Колод пока нет"
          description="Создайте колоду и загрузите изображения карт."
          action={<Button onClick={() => setCreating(true)}>Новая колода</Button>}
        />
      ) : (
        <Paper withBorder radius="lg" style={{ borderColor: 'var(--border-subtle)' }}>
          <ScrollArea>
            <Table verticalSpacing="sm" horizontalSpacing="md" miw={860} highlightOnHover>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th w={112}>Обложка и рубашка</Table.Th>
                  <Table.Th>Колода</Table.Th>
                  <Table.Th>Карт</Table.Th>
                  <Table.Th>Статус</Table.Th>
                  <Table.Th>Свободный стол</Table.Th>
                  <Table.Th>Доступ</Table.Th>
                  <Table.Th />
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {items.map((deck) => (
                  <Table.Tr key={deck.id}>
                    <Table.Td>
                      {/*
                        Обложка и рубашка рядом: в списке была только обложка, и
                        рубашку — вторую половину того, что видит человек за
                        столом, — приходилось искать, открывая каждую колоду.

                        Обе показываются целиком, а не подогнанными под рамку:
                        приходят они разной формы, и обрезанная по краям колода
                        узнаётся хуже, чем уменьшенная.
                      */}
                      <Group gap={6} wrap="nowrap">
                        <Tooltip label="Обложка" withArrow>
                          <div style={{ width: 44 }}>
                            <AssetImage
                              src={deck.coverImage}
                              alt=""
                              ratio="7 / 12"
                              fit="contain"
                              fallbackLabel=""
                            />
                          </div>
                        </Tooltip>
                        <Tooltip label="Рубашка" withArrow>
                          <div style={{ width: 44 }}>
                            <AssetImage
                              src={deck.cardBackImage}
                              alt=""
                              ratio="7 / 12"
                              fit="contain"
                              fallbackLabel=""
                            />
                          </div>
                        </Tooltip>
                      </Group>
                    </Table.Td>

                    <Table.Td>
                      <Stack gap={2}>
                        <Text fw={600} size="sm">
                          {deck.title}
                        </Text>
                        <Text size="xs" c="var(--text-subtle)">
                          {deck.slug} · {deck.kind === 'game' ? 'игровая' : 'обычная'}
                        </Text>
                      </Stack>
                    </Table.Td>

                    <Table.Td>
                      <Text size="sm">{deck.cardCount}</Text>
                    </Table.Td>

                    <Table.Td>
                      <Badge
                        size="sm"
                        variant={deck.status === 'published' ? 'light' : 'default'}
                        color={deck.status === 'published' ? 'teal' : 'gray'}
                      >
                        {DECK_STATUS_LABELS[deck.status]}
                      </Badge>
                    </Table.Td>

                    <Table.Td>
                      <Text size="sm" c={deck.availableInFreeform ? undefined : 'var(--text-subtle)'}>
                        {deck.availableInFreeform ? 'да' : 'нет'}
                      </Text>
                    </Table.Td>

                    <Table.Td>
                      <Stack gap={4} align="flex-start">
                        {deck.accessRules.length === 0 ? (
                          <Badge size="xs" variant="light" color="gold">
                            не настроен
                          </Badge>
                        ) : (
                          <Group gap={4}>
                            {deck.accessRules.map((rule) => (
                              <Badge key={rule} size="xs" variant="default">
                                {ACCESS_RULE_LABELS[rule]}
                              </Badge>
                            ))}
                          </Group>
                        )}
                        <Button
                          size="compact-xs"
                          variant="subtle"
                          onClick={() => void navigate(`/admin/decks/${deck.id}?tab=access`)}
                          data-testid="open-access"
                        >
                          Настроить доступ
                        </Button>
                      </Stack>
                    </Table.Td>

                    <Table.Td>
                      <Button
                        size="xs"
                        variant="light"
                        onClick={() => void navigate(`/admin/decks/${deck.id}`)}
                      >
                        Открыть
                      </Button>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </ScrollArea>
        </Paper>
      )}

      <Modal opened={creating} onClose={() => setCreating(false)} title="Новая колода">
        <form onSubmit={form.onSubmit((values) => createMutation.mutate(values))}>
          <Stack gap="md">
            <TextInput label="Название" placeholder="Например, Таланты" required {...form.getInputProps('title')} />
            <Textarea
              label="Описание"
              placeholder="Для чего эта колода"
              minRows={2}
              {...form.getInputProps('description')}
              value={form.values.description ?? ''}
              onChange={(event) => form.setFieldValue('description', event.currentTarget.value)}
            />
            <Select
              label="Тип"
              data={[
                { value: 'generic', label: 'Обычная — доступна на свободном столе' },
                { value: 'game', label: 'Игровая — только для секторов игры' },
              ]}
              {...form.getInputProps('kind')}
            />
            <Text size="xs" c="var(--text-muted)">
              Колода создаётся черновиком. Загрузите карты и настройте доступ, затем опубликуйте.
            </Text>
            <Group justify="flex-end">
              <Button variant="default" onClick={() => setCreating(false)}>
                Отмена
              </Button>
              <Button type="submit" loading={createMutation.isPending}>
                Создать
              </Button>
            </Group>
          </Stack>
        </form>
      </Modal>
    </>
  );
}
